import React from "react";
import { motion } from "framer-motion";
import AdinkraMark from "@/components/AdinkraMark";

// A living constellation of the 8 module symbols. Each ignites gold as its
// module completes; a gold chain (nkonsonkonson) links the ones you've earned.
// nodes: [{ id, sym, n, title, done }]
const POS = [
  { x: 7, y: 62 }, { x: 20, y: 30 }, { x: 32, y: 66 }, { x: 45, y: 34 },
  { x: 58, y: 68 }, { x: 70, y: 32 }, { x: 81, y: 60 }, { x: 92, y: 30 },
];

export default function AdinkraConstellation({ nodes = [], celebrate = false }) {
  const pts = nodes.slice(0, POS.length).map((n, i) => ({ ...n, ...POS[i] }));
  const sparks = Array.from({ length: 22 });
  return (
    <div className="relative w-full" data-testid="adinkra-constellation" style={{ aspectRatio: "5 / 1.35" }}>
      {celebrate && (
        <div className="pointer-events-none absolute inset-0 z-20 overflow-visible" data-testid="constellation-burst">
          {/* persistent gold aura across the whole chain so the celebration always reads */}
          <motion.span
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[120%]"
            style={{ background: "radial-gradient(60% 120% at 50% 50%, rgba(212,175,55,0.22), rgba(212,175,55,0) 70%)" }}
            animate={{ opacity: [0.55, 0.9, 0.55] }}
            transition={{ duration: 3.2, ease: "easeInOut", repeat: Infinity }}
          />
          {/* one-shot bright flash on mount */}
          <motion.span
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ width: 40, height: 40, background: "radial-gradient(circle, rgba(255,246,214,0.95), rgba(212,175,55,0) 70%)" }}
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: 30, opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
          />
          {/* recurring pulse ring */}
          <motion.span
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ width: 24, height: 24, background: "radial-gradient(circle, rgba(232,212,139,0.9), rgba(212,175,55,0) 70%)" }}
            initial={{ scale: 0, opacity: 0.85 }}
            animate={{ scale: [0, 26, 34], opacity: [0.85, 0.35, 0] }}
            transition={{ duration: 2.4, ease: "easeOut", repeat: Infinity, repeatDelay: 0.8 }}
          />
          {sparks.map((_, i) => {
            const ang = (i / sparks.length) * Math.PI * 2;
            const dist = 130 + (i % 4) * 46;
            return (
              <motion.span
                key={i}
                className="absolute left-1/2 top-1/2 rounded-full bg-gold-pale"
                style={{ width: 5, height: 5, boxShadow: "0 0 10px 2px rgba(212,175,55,0.8)" }}
                initial={{ x: 0, y: 0, opacity: 0 }}
                animate={{ x: Math.cos(ang) * dist, y: Math.sin(ang) * dist * 0.5, opacity: [0, 1, 0] }}
                transition={{ duration: 1.9, ease: "easeOut", repeat: Infinity, repeatDelay: 1.4, delay: (i % 6) * 0.08 }}
              />
            );
          })}
        </div>
      )}
      <svg viewBox="0 0 100 30" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        {pts.slice(0, -1).map((p, i) => {
          const q = pts[i + 1];
          const lit = p.done && q.done;
          return (
            <motion.line
              key={i}
              x1={p.x} y1={p.y * 0.3} x2={q.x} y2={q.y * 0.3}
              stroke={lit ? "#D4AF37" : "rgba(255,255,255,0.14)"}
              strokeWidth={lit ? 0.5 : 0.35}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 + i * 0.08 }}
            />
          );
        })}
      </svg>
      {pts.map((p, i) => (
        <motion.div
          key={p.id}
          className="absolute -translate-x-1/2 -translate-y-1/2 group"
          style={{ left: `${p.x}%`, top: `${p.y}%` }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15 + i * 0.09, type: "spring", stiffness: 220, damping: 18 }}
          title={`Module ${p.n} · ${p.title}${p.done ? " ✓" : ""}`}
        >
          {p.done && <span className="htt-halo absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.5), transparent 70%)" }} />}
          <span className={`relative flex items-center justify-center rounded-full w-11 h-11 sm:w-12 sm:h-12 border transition-colors duration-500 ${p.done ? "bg-purple-deep border-gold" : "bg-purple-deep/70 border-white/20"}`}>
            <AdinkraMark symbol={p.sym} className={p.done ? "text-gold" : "text-white/55"} style={{ width: "22px", height: "22px" }} />
          </span>
          <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 text-[10px] uppercase tracking-[0.14em] font-bold text-gold-pale/75 whitespace-nowrap">{p.n}</span>
        </motion.div>
      ))}
    </div>
  );
}
